// TimeSheetsSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { apiFetch } from '../../utils/api';
import { Logger } from '../../utils/logger';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const logger = new Logger('TimeSheetsSection');

const TimeSheetsSection = ({ timesheetData = {}, employeeId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [timePeriod, setTimePeriod] = useState('last_year');
  const [filteredData, setFilteredData] = useState(timesheetData);
  const [isLoading, setIsLoading] = useState(false);
  const [weeklySort, setWeeklySort] = useState({ field: 'week_ending', dir: 'desc' });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle time period change
  const handleTimePeriodChange = (event) => {
    if (event && event.target) {
      const newPeriod = event.target.value;
      setTimePeriod(newPeriod);
      
      // If we have an employee ID, fetch the data with the new period
      if (employeeId) {
        fetchTimesheetData(employeeId, newPeriod);
      }
    }
  };
  
  // Function to fetch timesheet data with a specific period
  const fetchTimesheetData = async (empId, period) => {
    if (!empId) return;
    
    setIsLoading(true);
    try {
      // Ensure employee ID has the EMP prefix if needed
      const formattedEmployeeId = empId.toString().startsWith('EMP') ? 
        empId : `EMP${empId}`;
      
      const response = await apiFetch(`employees/${formattedEmployeeId}/timesheet-summary?period=${period}`);

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      let data = await response.json();

      // If the response is a string (from bson.json_util.dumps), try to parse it
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          logger.error('Error parsing timesheet data:', parseError);
        }
      }
      
      logger.debug('Fetched timesheet data for period:', period, data);
      setFilteredData(data);
    } catch (error) {
      logger.error('Error fetching timesheet data:', error);
      // Keep the current data if there's an error
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch with the default period on mount or when the employee changes.
  // Period changes are handled by handleTimePeriodChange — do NOT add
  // timePeriod here or every select change triggers a double-fetch and a
  // stale-reset back to the all-time prop data.
  useEffect(() => {
    if (employeeId) {
      fetchTimesheetData(employeeId, timePeriod);
    } else {
      setFilteredData(timesheetData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetData, employeeId]);

  // Ensure we have valid data before rendering
  if (!timesheetData || typeof timesheetData !== 'object' || Object.keys(timesheetData).length === 0) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Time Sheets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No timesheet data available
        </Typography>
      </Box>
    );
  }

  // Extract data for easier access from the filtered data
  const {
    total_billable_hours = 0,
    total_internal_hours = 0,
    average_utilization = 0,
    projects_summary = [],
    weekly_timesheets = [],
    monthly_summaries = []
  } = filteredData || timesheetData;

  const toggleWeeklySort = (field) => {
    setWeeklySort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'week_ending' ? 'desc' : 'desc' }
    );
  };

  const sortedWeeklyTimesheets = [...(weekly_timesheets || [])].sort((a, b) => {
    const { field, dir } = weeklySort;
    let av = a[field] ?? '';
    let bv = b[field] ?? '';
    if (field === 'week_ending') {
      av = String(av);
      bv = String(bv);
    } else {
      av = Number(av) || 0;
      bv = Number(bv) || 0;
    }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (weeklySort.field !== field) return null;
    return weeklySort.dir === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: '0.75rem', ml: 0.5, verticalAlign: 'middle' }} />
      : <ArrowDownwardIcon sx={{ fontSize: '0.75rem', ml: 0.5, verticalAlign: 'middle' }} />;
  };

  const sortableHeaderSx = { cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } };

  // Format utilization as percentage
  const formatUtilization = (value) => {
    return `${Math.round(value)}%`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle MongoDB date format which might come as an object with $date property
    if (typeof dateString === 'object' && dateString.$date) {
      dateString = dateString.$date;
    }
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC' // Force UTC to avoid timezone shifts
    }).format(date);
  };



  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Time Sheets
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isLoading && (
            <CircularProgress size={24} sx={{ mr: 2 }} />
          )}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
              disabled={isLoading}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="last_quarter">Last Quarter</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Client Billable Hours
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
              {total_billable_hours.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Internal Hours
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
              {(total_internal_hours || 0).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Average Utilization
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
              {formatUtilization(average_utilization)}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Projects Worked
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
              {projects_summary.filter(p => !p.is_internal).length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Box sx={{ width: '100%', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Weekly Timesheets" />
          <Tab label="Project Breakdown" />
          <Tab label="Monthly Summary" />
        </Tabs>
      </Box>

      {/* Weekly Timesheets Tab */}
      {activeTab === 0 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={sortableHeaderSx} onClick={() => toggleWeeklySort('week_ending')}>
                    Week Ending<SortIcon field="week_ending" />
                  </TableCell>
                  <TableCell>Projects</TableCell>
                  <TableCell align="right" sx={sortableHeaderSx} onClick={() => toggleWeeklySort('billable_hours')}>
                    Client Hours<SortIcon field="billable_hours" />
                  </TableCell>
                  <TableCell align="right" sx={sortableHeaderSx} onClick={() => toggleWeeklySort('internal_hours')}>
                    Internal Hours<SortIcon field="internal_hours" />
                  </TableCell>
                  <TableCell align="right">Utilization</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(sortedWeeklyTimesheets) && sortedWeeklyTimesheets.map((week, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{formatDate(week.week_ending)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.isArray(week.projects) && week.projects.map((project, i) => (
                          <Chip 
                            key={i} 
                            label={`${project.client_name || 'Unknown Client'} - ${project.project_name || 'Unknown Project'} (${project.hours || 0}h)`} 
                            size="small" 
                            sx={{ 
                              bgcolor: project.is_internal ? 'rgba(255, 152, 0, 0.1)' : 'rgba(3, 218, 198, 0.1)', 
                              color: project.is_internal ? '#FF9800' : '#03DAC6',
                              fontSize: '0.7rem'
                            }} 
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{week.billable_hours || 0}</TableCell>
                    <TableCell align="right">{week.internal_hours || 0}</TableCell>
                    <TableCell align="right">{formatUtilization(week.utilization || 0)}</TableCell>
                  </TableRow>
                ))}
                {sortedWeeklyTimesheets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No timesheet data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Project Breakdown Tab */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" gutterBottom>
            Project Details
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(projects_summary) && projects_summary.map((project, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{project.project_name || 'Unknown Project'}</TableCell>
                    <TableCell>{project.client_name || 'Unknown Client'}</TableCell>
                    <TableCell align="right">{project.total_hours || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={project.is_internal ? "Internal" : "Client"} 
                        size="small" 
                        sx={{ 
                          bgcolor: project.is_internal ? 'rgba(255, 152, 0, 0.1)' : 'rgba(3, 218, 198, 0.1)', 
                          color: project.is_internal ? '#FF9800' : '#03DAC6',
                          fontSize: '0.7rem'
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {projects_summary.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No project data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Monthly Summary Tab */}
      {activeTab === 2 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" gutterBottom>
            Monthly Hours
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Billable Hours</TableCell>
                  <TableCell align="right">Available Hours</TableCell>
                  <TableCell align="right">Utilization</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(monthly_summaries) && monthly_summaries.map((month, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{month.month || 'Unknown'}</TableCell>
                    <TableCell align="right">{month.billable_hours || 0}</TableCell>
                    <TableCell align="right">{month.available_hours || 0}</TableCell>
                    <TableCell align="right">{formatUtilization(month.utilization || 0)}</TableCell>
                  </TableRow>
                ))}
                {monthly_summaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No monthly data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default TimeSheetsSection;
